import { PagedOrganisationResponse, useGetPaginatedOrganisations } from 'api';
import { useAuth } from 'contexts/AuthContext';
import { useEffect } from 'react';
import { QueryStatus } from '@tanstack/react-query';

export type OrganisationsLoad = 'none' | 'firstPage' | 'allPages';

export type MyOrganisationsResult = {
  organisations: PagedOrganisationResponse[] | undefined;
  totalCount: number | undefined;
  status: QueryStatus;
  isLoading: boolean;
  hasMultipleOrgs: boolean;
};

const useGetMyOrganisations = ({
  load = 'firstPage',
}: { load?: OrganisationsLoad } = {}): MyOrganisationsResult => {
  const { user } = useAuth();
  const enabled = load !== 'none';

  const {
    data,
    status: organisationsStatus,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetPaginatedOrganisations({ enabled });

  const hasMorePagesToLoad =
    load === 'allPages' && hasNextPage && organisationsStatus === 'success';
  const shouldFetchNextPage = hasMorePagesToLoad && !isFetchingNextPage;

  useEffect(() => {
    if (shouldFetchNextPage) {
      fetchNextPage();
    }
  }, [shouldFetchNextPage, fetchNextPage, data]);

  const status: QueryStatus = hasMorePagesToLoad
    ? 'pending'
    : organisationsStatus;

  const organisations = data?.items;
  const totalCount = data?.totalCount;

  const hasMultipleOrgs =
    user?.permissions.Organisation_Create === 'Global' ||
    (totalCount !== undefined && totalCount > 1);

  return {
    organisations,
    totalCount,
    hasMultipleOrgs,
    status,
    isLoading,
  };
};

export default useGetMyOrganisations;
